/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, May 6th 2022, 2:42:18 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Skeleton from "@mui/material/Skeleton";
import FormControl from "@mui/material/FormControl";
import Tooltip from "@mui/material/Tooltip";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import { TextFormInputProps, TextFormInput } from "ui-widgets";
import { useOSFormContext } from "ui-widgets/forms/providers";
import { useControlledInput } from "ui-widgets/forms";
import {
  APIFormInputErrorWrapper,
  CustomInputValidationErrorWrapper,
  HelperTextInputWrapper,
} from "ui-widgets/forms/FormInputWrappers";
import { usePortalsAPI } from "portals/services/PortalsAPI";
import { Portal } from "lib/schemas";
import { isDomainName } from "lib/validation";
import { useCopyTextToClipBoard } from "lib/utils";
import { APIItemDisplayWrapper } from "ui-widgets";

const replaceDotWithDash = (value: string) => value?.replace(".", "-");

export const OneStopSubdomainInput = <TFormData,>(props: TextFormInputProps<TFormData>) => {
  const [mainSite, setMainSite] = React.useState<Portal>();
  const { useAPIGetMainSite } = usePortalsAPI();

  const domainSuffix = mainSite?.domain ? `.${mainSite.domain}` : "";

  const mainSiteIsDomainName = !mainSite || isDomainName(mainSite?.domain?.split(":")?.[0]); // !mainSite condition is so that the loader can appear when main site is being fetched

  const { isMakingAPIRequest, httpResponseStatusCode, isAPIError } = useAPIGetMainSite({
    setValidAPIResponse: setMainSite,
  });

  const { setValue } = useOSFormContext<TFormData>();
  const {
    field: { value, onBlur, ref },
  } = useControlledInput<TFormData>(props);

  const displayValue = replaceDotWithDash((value as string)?.split(domainSuffix)?.[0]); //. should not be present in the user chosen domain name to avoid managing several layers of domain names in our network.

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fullDomainName = `${replaceDotWithDash(e.target.value)}${domainSuffix}` as any;
    setValue(props.schema_field_key as any, fullDomainName);
  };

  const { copied, copyTextToClipBoard } = useCopyTextToClipBoard();

  return mainSiteIsDomainName ? (
    <APIItemDisplayWrapper
      showLoader={isMakingAPIRequest}
      isAPIError={isAPIError}
      httpResponseStatusCode={httpResponseStatusCode}
      loaderType="custom"
      customProgressLoader={<OneStopSubdomainInputSkeleton />}
    >
      <APIFormInputErrorWrapper schema_field_key={props.schema_field_key} label={props.label}>
        <CustomInputValidationErrorWrapper schema_field_key={props.schema_field_key} label={props.label}>
          <HelperTextInputWrapper helperText={props.helperText}>
            <Stack direction="row">
              <TextField
                label={props.label}
                placeholder={props.placeholder ?? "eg. myportal"}
                fullWidth={props.fullWidth}
                value={displayValue}
                helperText={null}
                ref={ref}
                onBlur={onBlur}
                onChange={handleOnChange}
                className="os-subdomain-input-text-field"
              />

              <Chip
                className="os-subdomain-input-chip"
                deleteIcon={
                  <Tooltip title="Copy URL">
                    <IconButton size="small" onClick={() => copyTextToClipBoard(value as string)}>
                      <ContentCopyIcon fontSize="small" color="secondary" />
                    </IconButton>
                  </Tooltip>
                }
                label={domainSuffix}
                onDelete={() => {}}
              />
            </Stack>
            <Snackbar open={copied} autoHideDuration={6000} message="URL Copied" />
          </HelperTextInputWrapper>
        </CustomInputValidationErrorWrapper>
      </APIFormInputErrorWrapper>
    </APIItemDisplayWrapper>
  ) : (
    <TextFormInput {...props} />
  );
};

const OneStopSubdomainInputSkeleton = () => {
  return (
    <FormControl>
      <Skeleton animation="wave" variant="rectangular" height={53} />
    </FormControl>
  );
};
